import {
    Button,
    Divider,
    DrawerFooter,
    DrawerHeaderTitle,
    makeStyles,
    Spinner
} from '@fluentui/react-components';
import {
    DrawerOverlay,
    DrawerBody,
    DrawerHeader
} from "@fluentui/react-components/unstable";
import { useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { DrawerContent } from './DrawerContent/DrawerContent';
import { ProgressIndicator } from './ProgressIndicator/ProgressIndicator';
import type { ICreateConfigurationInfo, IUserDetail, IGroupDetail, IGraphPickerItem } from '../../interfaces';
import { EUserSelection, EAppId, configurableApplication } from '../../interfaces';
import { createConfigurationAsync } from '../../apiRequests/settings/settings';
import { getUserAndGroupDetailsFromSelectedPeople } from '../../utilities/SettingsHelper/SettingsHelper';
import { getDefaultSettings } from '../../utilities/ConfigurationData/ConfigurationData';
import { InfoLabelWithLink } from '../InfoLabelWithLink/InfoLabelWithLink';

const useStyles = makeStyles({
    body: {
        display: 'flex',
        alignItems: 'stretch',
        height: '100%',
        overflow: 'hidden'
    },
    leftDiv: {
        width: '20%',
        height: '100%',
        flexShrink: 0,
        overflow: 'hidden'
    },
    rightDiv: {
        flexGrow: 1,
        height: '100%',
        overflowY: 'auto'
    },
    footer: {
        justifyContent: 'space-between',
    },
    navigateButtons: {
        display: 'flex',
        gap: '10px'
    },
    horizontalDivider: {
        width: "100%"
    },
    horizontalDividerTop: {
        paddingTop: '20px',
        width: "100%"
    },
    verticalDivider: {
        minHeight: '100%',
        minWidth: '50px',
    },
    contentContainer: {
        paddingTop: '30px'
    },
    settingsDivHeader: {
        paddingBottom: '10px'
    }
});


export interface ICreateConfigurationDrawerProps {
    onOpenChange(isOpen: boolean): void;
}

export type configurationCreationStep = "application" | "settings" | "users" | "finish";

export const CreateConfigurationDrawer = (props: ICreateConfigurationDrawerProps) => {
    const { onOpenChange } = props;
    const { instance, accounts } = useMsal();
    const account = accounts[0];

    const [currentSettingsCreationContent, setCurrentSettingsCreationContent] = useState<configurationCreationStep>("application");
    const [buttonLabel, setButtonLabel] = useState("Next");

    const [isBackButtonVisible, setIsBackButtonVisible] = useState(false);
    const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(false);
    const [isCancelButtonVisible, setIsCancelButtonVisible] = useState(true);
    const [isNextButtonVisible, setIsNextButtonVisible] = useState(true);

    const [isLoading, setIsLoading] = useState(false);

    const [settingsInfo, setSettingsInfo] = useState<ICreateConfigurationInfo>({ appId: EAppId.Mail, settingsData: getDefaultSettings(EAppId.Mail), isEnabled: true, tenantId: account?.tenantId });
    const [selectedPeople, setSelectedPeople] = useState<IGraphPickerItem[]>([]);
    const [usersToBeAdded, setUsersToBeAdded] = useState<IUserDetail[]>([]);
    const [groupsToBeAdded, setGroupsToBeAdded] = useState<IGroupDetail[]>([]);
    const [userSelectionValue, setUserSelectionValue] = useState(EUserSelection.Organization);

    const styles = useStyles();

    const validateApplicationStep = useCallback((): boolean => {
        return !!settingsInfo.appId;
    }, [settingsInfo.appId]);

    const validateSettingsStep = useCallback((): boolean => {
        return !!settingsInfo.settingsData && Object.keys(settingsInfo.settingsData).length > 0;
    }, [settingsInfo.settingsData]);

    const validateUsersStep = useCallback((): boolean => {
        if (userSelectionValue === EUserSelection.User) {
            return usersToBeAdded.length > 0;
        }
        if (userSelectionValue === EUserSelection.Group) {
            return groupsToBeAdded.length > 0 || usersToBeAdded.length > 0;
        }
        return true;
    }, [userSelectionValue, usersToBeAdded, groupsToBeAdded]);

    const validateFinishStep = useCallback((): boolean => {
        return validateApplicationStep() && validateSettingsStep() && validateUsersStep();
    }, [validateApplicationStep, validateSettingsStep, validateUsersStep]);

    useEffect(() => {
        let isValid = false;

        switch (currentSettingsCreationContent) {
            case "application":
                isValid = validateApplicationStep();
                setIsBackButtonVisible(false);
                break;
            case "settings":
                isValid = validateSettingsStep();
                setButtonLabel("Next");
                setIsBackButtonVisible(true);
                break;
            case "users":
                isValid = validateUsersStep();
                setButtonLabel("Next");
                break;
            case "finish":
                isValid = validateFinishStep();
                setButtonLabel("Create");
                break;
        }

        setIsNextButtonDisabled(!isValid);
    }, [currentSettingsCreationContent, settingsInfo, userSelectionValue, usersToBeAdded, groupsToBeAdded, validateApplicationStep, validateSettingsStep, validateUsersStep, validateFinishStep]);

    const createNewConfigurationAsync = async (): Promise<void> => {
        try {
            await createConfigurationAsync(instance, account, settingsInfo);
        } catch (error) {
            console.debug(error);
        } finally {
            onOpenChange(false);
        }
    };

    const onApplicationSelected = (application: EAppId): void => {
        setSettingsInfo((previous) => ({ ...previous, appId: application, settingsData: getDefaultSettings(application) }));
    };

    const onUserSelectionChanged = (value: EUserSelection): void => {
        setUserSelectionValue(value);
        if (value === EUserSelection.Organization) {
            setSelectedPeople([]);
            setUsersToBeAdded([]);
            setGroupsToBeAdded([]);
        }
    };

    const onSettingsInfoUpdated = (newSettingsInfo: ICreateConfigurationInfo): void => {
        setSettingsInfo(newSettingsInfo);
    };

    const onEnabledChanged = (isEnabled: boolean): void => {
        setSettingsInfo((previous) => ({ ...previous, isEnabled }));
    };

    const onDescriptionChanged = (description: string): void => {
        setSettingsInfo((previous) => ({ ...previous, description: description }));
    };

    const onPeopleSelected = (people: IGraphPickerItem[]): void => {
        const result = getUserAndGroupDetailsFromSelectedPeople(people);
        const groups = result.groupDetails;
        const users = result.userDetails;

        setUsersToBeAdded(users);
        setGroupsToBeAdded(groups);
        setSelectedPeople(people);
        setSettingsInfo((previous) => ({ ...previous, groupIds: groups.map(g => g.id), userIds: users.map(g => g.id) }));
    };

    const onNextButtonPressed = async (): Promise<void> => {
        switch (currentSettingsCreationContent) {
            case "application":
                setCurrentSettingsCreationContent("settings");
                return;

            case "settings":
                setCurrentSettingsCreationContent("users");
                return;

            case "users":
                setCurrentSettingsCreationContent("finish");
                return;

            case "finish":
                setIsLoading(true);
                setIsCancelButtonVisible(false);
                setIsBackButtonVisible(false);
                setIsNextButtonVisible(false);
                await createNewConfigurationAsync();
                return;
        }
    };

    const onBackButtonPressed = (): void => {
        switch (currentSettingsCreationContent) {
            case "application":
            case "settings":
                setCurrentSettingsCreationContent("application");
                return;

            case "users":
                setCurrentSettingsCreationContent("settings");
                return;

            case "finish":
                setCurrentSettingsCreationContent("users");
                return;
        }
    };

    const renderDrawerContentTitle = (): ReactNode => {
        const titles: Record<string, ReactNode> = {
            application: <h3>Select application</h3>,
            settings: (
                <div className={styles.settingsDivHeader}>
                    <h3>Configure settings for {configurableApplication[settingsInfo.appId as EAppId]}</h3>
                    <InfoLabelWithLink appId={settingsInfo.appId as EAppId} showInfoLabel />
                </div>
            ),
            users: <h3>Assign users</h3>,
        };

        return titles[currentSettingsCreationContent] || null;
    };

    const renderDrawerBodyMainContent = () => {
        return (
            <div className={styles.body}>
                <div className={styles.leftDiv}>
                    <ProgressIndicator currentProgress={currentSettingsCreationContent} />
                </div>
                <div>
                    <Divider vertical className={styles.verticalDivider} />
                </div>
                <div className={styles.rightDiv}>
                    <div className={styles.contentContainer}>
                        {renderDrawerContentTitle()}
                        <DrawerContent
                            currentDialog={currentSettingsCreationContent}
                            settingsInfo={settingsInfo}
                            selectedPeople={selectedPeople}
                            usersToBeAdded={usersToBeAdded}
                            groupsToBeAdded={groupsToBeAdded}
                            onPeopleSelect={onPeopleSelected}
                            onSettingsInfoChange={onSettingsInfoUpdated}
                            onApplicationSelect={onApplicationSelected}
                            onUserSelectionChange={onUserSelectionChanged}
                            onEnabledChange={onEnabledChanged}
                            onDescriptionChange={onDescriptionChanged}
                            userSelectionValue={userSelectionValue} />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <DrawerOverlay
            modalType='alert'
            size="large"
            style={{ width: `75%` }}
            position="end"
            open>
            <DrawerHeader>
                <DrawerHeaderTitle>
                    Create new configuration
                </DrawerHeaderTitle>
            </DrawerHeader>
            <div className={styles.horizontalDividerTop}>
                <Divider />
            </div>
            <DrawerBody>
                {renderDrawerBodyMainContent()}
            </DrawerBody>
            <div className={styles.horizontalDivider}>
                <Divider />
            </div>
            <DrawerFooter className={styles.footer}>
                {isLoading && <Spinner size="extra-small" label={"Creating..."} />}
                <div className={styles.navigateButtons}>
                    {isBackButtonVisible && <Button
                        appearance="secondary"
                        onClick={() => onBackButtonPressed()}>
                        Back
                    </Button>}
                    {isNextButtonVisible && <Button
                        appearance="primary"
                        disabled={isNextButtonDisabled}
                        onClick={() => onNextButtonPressed()}>
                        {buttonLabel}
                    </Button>}
                </div>
                {isCancelButtonVisible && <Button
                    appearance="secondary"
                    onClick={() => onOpenChange(false)}>
                    Cancel
                </Button>}
            </DrawerFooter>
        </DrawerOverlay>
    );
};
